import { Basemaps, Condition, Input } from "./enums";

export interface IConfig {
    default: IDefault;
    basemaps: (IBasemapTileXYZ | IBasemapTileWMS)[];
    layers: ILayerConfig[];
    geoLocation?: ILocation;
    forms: IForms;
    search: ISearchLayer[];
    identify: IIdentifyConfig;
    reCaptchaSiteKey: string;
    upload: IUploadConfig;
    print: IPrintConfig;
    email: IEmailConfig;
    geometryService: IGeometryServiceConfig;
}
export interface IDefault {
    center: number[];
    extentSize: number[];
    viewExtent: number[];
    zoom: number;
    minZoom: number;
    maxZoom: number;
    projection: string;
    zoomToLevel: number;
    highLightLayerId: string;
    drawingLayerId: string;
    tileGrid: ITileGrid;
}

export interface ITileGrid {
    origin: number[];
    tileSize?: number[];
    resolutions: number[];
}

export interface IBasemapTileXYZ {
    id: string;
    title: string;
    type: string;
    visible: boolean;
    image: string;
    source: {
        url: string;
        maxZoom: number;
        projection: string;
        crossOrigin: string;
    };
}
export interface IBasemapTileWMS {
    id: string;
    title: string;
    type: string;
    visible: boolean;
    image: string;
    source: {
        url: string;
        params?: ILayerParams;
        projection: string;
        serverType?: string;
        crossOrigin: string;
    };
}
export interface ILayerConfig {
    id: string;
    title: string;
    type: string;
    copyright: string;
    url: string;
    crossOrigin: string;
    visible: boolean;
    showInToc: boolean;
    zIndex: number;
    minResolution?: number;
    maxResolution?: number;
    extent?: number[];
    params?: ILayerParams;
}

export interface ILayerParams {
    LAYERS?: string;
    FORMAT?: string;
    transparent?: boolean;
}

export interface ILocation {
    geolocationOptions?: {
        maximumAge: number;
        timeout: number;
        enableHighAccuracy: boolean;
    };
}

export interface IIdentify {
    fields: string[];
    conditionalFields: IIdentifyCondition[];
}

export interface IIdentifyCondition {
    field: string;
    value: string | number | boolean;
    condition: Condition;
    showFields: string[];
}

export interface IForms {
    textFilter: IInputFieldConfig;
    individualApplicantForm: IFormConfig;
    companyApplicantForm: IFormConfig;
    termForm: IFormConfig;
}

export interface IFormConfig {
    fields: IInputFieldConfig[];
}
export interface IInputFieldConfig {
    field: string;
    title: string;
    type?: Input;
    autocomplete?: string;
    required?: boolean;
    validationFn?: (value, comparator?: string) => boolean;
    validationErrorMessage?: string;
    validationComparator?: string;
    visibilityDeterminantField?: string;
    visibilityDeterminantFieldVisibleValue?: string | boolean | number;
    info?: string;
    minDateField?: string;
    maxDateField?: string;
    minDate?: string;
    maxDate?: string;
    hasError?: boolean;
    maxlength?: number; // max length for Text or TextArea fields
}

export interface ISearchLayer {
    url: string;
    layerId: string;
    field: string;
}

export interface IIdentifyConfig {
    url: string;
    layers: string[];
}

export interface IUploadConfig {
    url: string;
    layerName: string;
    relatedTable: string;
}

export interface IPrintConfig {
    endpoint: string;
    url: string;
}

export interface IEmailConfig {
    subject: string;
    body: string;
    url: string;
}
export interface IGeometryServiceConfig {
    endpoint: string;
    url: string;
}

export const proxyUrl = process.env.NODE_ENV === "development" ? "https://lvmkartes.lvm.lv/timberflowform/beta/" : "";
export const all = "Izvēlēties vērtību";
export const defaultOption = { name: all, code: undefined };

export const config: IConfig = {
    default: {
        center: [530000, 310000],
        extentSize: [530000, 310000],
        viewExtent: [160000, 40000, 900000, 570000],
        zoom: 4.5,
        minZoom: 3.5,
        maxZoom: 20,
        projection: "EPSG:3059",
        zoomToLevel: 13,
        highLightLayerId: "highlightLayer",
        drawingLayerId: "drawingLayer",
        tileGrid: {
            origin: [-5120900, 3998100],
            tileSize: [512, 512],
            resolutions: [
                1058.3354500042335,
                529.1677250021168,
                264.5838625010584,
                132.2919312505292,
                52.91677250021167,
                26.458386250105836,
                13.229193125052918,
                10.583354500042335,
                7.9375158750317505,
                5.291677250021167,
                3.9687579375158752,
                2.6458386250105836,
                1.3229193125052918,
                0.5291677250021167
            ]
        }
    },
    basemaps: [
        {
            id: Basemaps.Mamma,
            title: "LVM Fona karte",
            type: "TileXYZ",
            visible: true,
            image: "mamma.png",
            source: {
                url: proxyUrl + "proxy/D341478CE74F4F02B68607991448D499/CloudMap_mammadaba/MapServer",
                maxZoom: 14,
                projection: "EPSG:3059",
                crossOrigin: "anonymous"
            }
        },
        {
            id: Basemaps.Orto,
            title: "Ortofoto karte",
            type: "TileWMS",
            visible: false,
            image: "orto2.png",
            source: {
                url: "https://lvmgeoserver.lvm.lv/geoserver/ows",
                params: {
                    LAYERS: "public:Orto_LKS",
                    FORMAT: "image/vnd.jpeg-png8",
                    transparent: false
                },
                projection: "EPSG:3059",
                serverType: "geoserver",
                crossOrigin: "anonymous"
            }
        },
        {
            id: Basemaps.Topo10,
            title: "Topogrāfiskā karte 1:10 000",
            type: "TileWMS",
            visible: false,
            image: "topo10.png",
            source: {
                url: "https://lvmgeoserver.lvm.lv/geoserver/ows",
                params: {
                    LAYERS: "public:Topo10",
                    FORMAT: "image/vnd.jpeg-png8",
                    transparent: false
                },
                projection: "EPSG:3059",
                serverType: "geoserver",
                crossOrigin: "anonymous"
            }
        }
    ],
    layers: [
        {
            id: "DAP",
            title: "Dabas aizsardzības pārvaldes dati",
            type: "ImageArcGISRest",
            copyright: "DAP",
            url: proxyUrl + "proxy/D341478CE74F4F02B68607991448D499/DAP/vDAP_public/MapServer",
            crossOrigin: "anonymous",
            maxResolution: 7.9375158750317505,
            visible: true,
            showInToc: true,
            zIndex: 10
        },
        {
            id: "cadaster",
            title: "Kadastra karte",
            type: "ImageArcGISRest",
            copyright: "Valsts Zemes dienests",
            url: proxyUrl + "proxy/7cb29dcbb61442b7a22c289b22d671b8/MapServer",
            crossOrigin: "anonymous",
            maxResolution: 20,
            params: { LAYERS: "show:2,4,17,20" },
            visible: true,
            showInToc: true,
            zIndex: 11
        },
        {
            id: "roadsLVM",
            title: "LVM autoceļi",
            type: "Vector",
            copyright: "LVM",
            url: proxyUrl + "proxy/db08b72d75954eeab9e3d3b19b03163d/NIsTimberFlow/FeatureServer/0",
            crossOrigin: "anonymous",
            maxResolution: 14.7, // 1000m
            visible: true,
            showInToc: true,
            zIndex: 12
        }
    ],
    geoLocation: {
        geolocationOptions: {
            maximumAge: 0,
            timeout: 15000,
            enableHighAccuracy: true
        }
    },
    forms: {
        textFilter: {
            field: "TEXT-FILTER",
            title: "Teksta meklēšana"
        },
        individualApplicantForm: {
            fields: [
                {
                    field: "APPLICANTNAME",
                    title: "Vārds, uzvārds",
                    type: Input.Text,
                    required: true,
                    maxlength: 100,
                    validationFn: (value): boolean => value.length <= 100,
                    validationErrorMessage: "Atļautas 100 zīmes"
                },
                {
                    field: "APPLICANTCODE",
                    title: "Personas kods",
                    type: Input.Text,
                    required: true,
                    validationFn: (value): boolean => {
                        //@ts-ignore
                        const result = window.PersonCodeValidator(value.replace(/[^0-9]/g, ""));
                        return result.isSuccess;
                    },
                    validationErrorMessage: "Kļūdains personas kods"
                },
                {
                    field: "APPLICANTADDRESS",
                    title: "Adrese",
                    type: Input.Text,
                    autocomplete: "street-address",
                    required: true,
                    maxlength: 100,
                    validationFn: (value): boolean => value.length <= 100,
                    validationErrorMessage: "Atļautas 100 zīmes"
                },
                {
                    field: "APPLICANTPHONE",
                    title: "Telefons",
                    type: Input.Phone,
                    autocomplete: "tel",
                    required: true,
                    validationFn: (value): boolean => (value.match(/^\+?\d{8,11}$/) ? true : false), // only digits, 8-11 symbols
                    validationErrorMessage: "Kļūdains telefona numurs"
                },
                {
                    field: "APPLICANTEMAIL",
                    title: "E-pasts",
                    type: Input.Email,
                    autocomplete: "email",
                    required: true,
                    validationFn: (value): boolean =>
                        value.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,6})+$/) ? true : false,
                    validationErrorMessage: "Kļūdains epasts"
                },
                { field: "OTHERCONTACTPERSON", title: "Cita kontaktpersona", type: Input.Checkbox },
                {
                    field: "CONTACTPERSON",
                    title: "Kontaktpersona",
                    type: Input.Text,
                    required: true,
                    visibilityDeterminantField: "OTHERCONTACTPERSON",
                    visibilityDeterminantFieldVisibleValue: true,
                    maxlength: 100,
                    validationFn: (value): boolean => value.length <= 100,
                    validationErrorMessage: "Atļautas 100 zīmes"
                },
                {
                    field: "CONTACTPERSONPHONE",
                    title: "Kontaktpersonas tālrunis",
                    type: Input.Phone,
                    autocomplete: "tel",
                    required: true,
                    visibilityDeterminantField: "OTHERCONTACTPERSON",
                    visibilityDeterminantFieldVisibleValue: true
                }
            ]
        },
        companyApplicantForm: {
            fields: [
                {
                    field: "APPLICANTNAME",
                    title: "Uzņēmuma nosaukums",
                    type: Input.Text,
                    autocomplete: "organization",
                    required: true,
                    maxlength: 100,
                    validationFn: (value): boolean => value.length <= 100,
                    validationErrorMessage: "Atļautas 100 zīmes"
                },
                {
                    field: "APPLICANTCODE",
                    title: "Reģistrācijas numurs",
                    type: Input.Text,
                    required: true,
                    validationFn: (value): boolean => value.length === 9 || value.length === 11,
                    validationErrorMessage: "Kļūdains reģistrācijas numurs"
                },
                {
                    field: "APPLICANTADDRESS",
                    title: "Adrese",
                    type: Input.Text,
                    required: true,
                    maxlength: 100,
                    autocomplete: "street-address",
                    validationFn: (value): boolean => value.length <= 100,
                    validationErrorMessage: "Atļautas 100 zīmes"
                },
                {
                    field: "APPLICANTPHONE",
                    title: "Telefons",
                    type: Input.Phone,
                    autocomplete: "tel",
                    required: true,
                    validationFn: (value): boolean => (value.match(/^\+?\d{8,11}$/) ? true : false), // only digits, 8-11 symbols
                    validationErrorMessage: "Kļūdains telefona numurs"
                },
                {
                    field: "APPLICANTEMAIL",
                    title: "E-pasts",
                    type: Input.Email,
                    autocomplete: "email",
                    required: true,
                    validationFn: (value: string, APPLICANTCODE: string): boolean => {
                        const isEmail = value.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/) ? true : false;
                        const [name, number] = value.split("@");
                        const isEadress = APPLICANTCODE
                            ? number === APPLICANTCODE && (number.length === 9 || number.length === 11)
                            : number.length === 9 || number.length === 11;
                        return isEmail || isEadress;
                    },
                    validationErrorMessage: "Kļūdains epasts",
                    validationComparator: "APPLICANTCODE"
                },
                {
                    field: "CONTACTPERSON",
                    title: "Kontaktpersona",
                    type: Input.Text,
                    required: true,
                    maxlength: 100,
                    validationFn: (value): boolean => value.length <= 100,
                    validationErrorMessage: "Atļautas 100 zīmes"
                },
                {
                    field: "CONTACTPERSONPHONE",
                    title: "Kontaktpersonas tālrunis",
                    type: Input.Phone,
                    autocomplete: "tel",
                    required: true,
                    validationFn: (value): boolean => (value.match(/^\+?\d{8,11}$/) ? true : false), // only digits, 8-11 symbols
                    validationErrorMessage: "Kļūdains telefona numurs"
                }
            ]
        },
        termForm: {
            fields: [
                {
                    field: "VOLUME",
                    title: "Kokmateriālu apjoms, m3",
                    type: Input.Number,
                    required: true,
                    maxlength: 8,
                    validationFn: (value): boolean => (value > 0 && value.match(/^[\d\.\,]{1,8}$/) ? true : false),
                    validationErrorMessage: "Nekorekts apjoms"
                },
                { field: "DATEFROM", title: "Datums no", type: Input.Date, required: true, maxDateField: "DATETO" },
                { field: "DATETO", title: "Datums līdz", type: Input.Date, required: true, minDateField: "DATEFROM" },
                {
                    field: "DESCRIPTION",
                    title: "Piezīmes",
                    type: Input.TextArea,
                    maxlength: 500,
                    validationFn: (value): boolean => value.length <= 500,
                    validationErrorMessage: "Atļautas 500 zīmes"
                }
            ]
        }
    },
    search: [
        // Pagasti
        { url: proxyUrl + "proxy/b3bab3192ed5477d8854f4eea8601f7f", layerId: "0", field: "MUNICIPALITYNAME" },
        // Novadi
        { url: proxyUrl + "proxy/b3bab3192ed5477d8854f4eea8601f7f", layerId: "1", field: "REGIONNAME" },
        // Autoceļi
        {
            url: proxyUrl + "proxy/db08b72d75954eeab9e3d3b19b03163d/NIsTimberFlow/FeatureServer",
            layerId: "0",
            field: "ROADNAME"
        },
        // Kadastra apzīmējums
        {
            url: proxyUrl + "proxy/db08b72d75954eeab9e3d3b19b03163d/NIsTimberFlow/FeatureServer",
            layerId: "1",
            field: "CODE"
        }
    ],
    identify: {
        url: proxyUrl + "proxy/db08b72d75954eeab9e3d3b19b03163d/NIsTimberFlow/MapServer",
        layers: ["1"]
    },
    reCaptchaSiteKey: "6LeaTM0iAAAAAKduC9nZtXMkNnTUc77q_c_Tlx6K",
    upload: {
        url: proxyUrl + "proxy/fp_save_65b3672bc95d445b839674948b5ad29f/api/Save",
        layerName: "PUBSDE.LVMFormTimberFlowFiles",
        relatedTable: "PUBSDE.LVMFormTimberFlowFiles"
    },
    print: {
        endpoint: "/print",
        url: proxyUrl + "proxy/print_846d8f58c3da4f91bc914f7fca62d0ec"
    },
    email: {
        subject: "Paziņojums par AS “Latvijas valsts meži” iesniegtu pieteikumu",
        body:
            "AS “Latvijas valsts meži” ir saņemts Jūsu iesniegums koksnes transportēšanas saskaņošanai. \n Ar cirsmas izstrādi saistīto darbību (sortimentu pievešana, nokraušana, transportēšana u.c.) LVM meža zemē un/vai meža ceļu izmantošanu drīkst uzsākt saņemot rakstisku LVM saskaņojumu uz elektroniskā pasta adresi vai pēc Vienošanās noslēgšanas.",
        url: proxyUrl + "proxy/email_6e1fcf2dbd3a482f9ca10cd08cc258af/api/send"
    },
    geometryService: {
        endpoint: "/api/geometryservice/intersect",
        url: proxyUrl + "proxy/geometry_1daf82b0fba14e20935e972913ff683b"
    }
};
